// src/screens/Retratos/Retratos.js
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import client from '../../sanity/SanityClient';
import imageUrlBuilder from '@sanity/image-url';
import './Retratos.css';

// Analytics
import { analytics } from '../../firebase/firebase';
import { logEvent } from 'firebase/analytics';

const builder = imageUrlBuilder(client);
const urlFor = (source) => builder.image(source);

const CATEGORIA = 'almasvisibles';
const SUBCATEGORIA_KEY = 'retratos';
const DESCRIPCION = `Más allá de la imagen, una ventana al alma. Cada retrato busca capturar la esencia, las emociones y la historia única de cada persona.`;

const PAGE_SIZE = 10;

// Claves de storage
const GRID_STATE_KEY = (pathname, search) => `gridState:${pathname}${search || ''}`; // y, page, sortKey, lastId, query
const ORDER_STATE_KEY = (pathname) => `gridOrder:${pathname}`; // orden base (_ids)

// ===== favoritos / login =====
const readFavs = () => {
  try { return new Set(JSON.parse(localStorage.getItem('favoritos') || '[]')); }
  catch { return new Set(); }
};
const saveFavs = (set) => localStorage.setItem('favoritos', JSON.stringify([...set]));
const isLoggedIn = () =>
  Boolean(
    localStorage.getItem('authUser') ||
    localStorage.getItem('user') ||
    localStorage.getItem('uid') ||
    localStorage.getItem('isLoggedIn') ||
    sessionStorage.getItem('authUser')
  );

// ===== orientación / layout =====
const ORIENT = { V: 'V', H: 'H' };

function fromAsset(imgField) {
  const asset = imgField?.asset;
  if (!asset) return null;
  const url = asset.url || urlFor(imgField).width(1200).auto('format').url();
  const dims = asset.metadata?.dimensions;
  const w = dims?.width ?? null;
  const h = dims?.height ?? null;
  let orientation = null;
  if (w && h) orientation = h > w ? ORIENT.V : ORIENT.H;
  return { url, w, h, orientation };
}

/** Normaliza a { _id, titulo, imageUrl, orientation, ratio, createdAt } */
function normalizeDocs(docs) {
  return docs
    .map((d) => {
      const primary = fromAsset(d.previewImage) || fromAsset(d.detalleImage);
      const imageUrl = primary?.url || null;
      let orientation = primary?.orientation ?? null;

      let ratio = null; // width/height
      if (primary?.w && primary?.h) ratio = primary.w / primary.h;
      else {
        const ar =
          d.previewImage?.asset?.metadata?.dimensions?.aspectRatio ??
          d.detalleImage?.asset?.metadata?.dimensions?.aspectRatio ?? null;
        if (typeof ar === 'number') {
          ratio = ar;
          orientation = orientation || (ar < 1 ? ORIENT.V : ORIENT.H);
        }
      }

      return {
        _id: d._id,
        titulo: d.titulo || '',
        imageUrl,
        orientation,
        ratio,
        createdAt: d._createdAt || null,
      };
    })
    .filter((x) => !!x.imageUrl);
}

/** Completa orientación/ratio con naturalWidth/Height cuando falte */
async function ensureOrientation(items) {
  const unresolved = items.filter((it) => !it.orientation || !it.ratio);
  await Promise.all(
    unresolved.map(
      (it) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const nw = img.naturalWidth || 1;
            const nh = img.naturalHeight || 1;
            if (!it.orientation) it.orientation = nh > nw ? ORIENT.V : ORIENT.H;
            if (!it.ratio) it.ratio = nw / nh;
            resolve();
          };
          img.onerror = () => resolve();
          img.src = it.imageUrl;
        })
    )
  );
  return items;
}

/** Mezcla (para orden base) */
function shuffle(items) {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Filas VHH / HVH / HHV con degradaciones seguras */
function buildRows(items) {
  const V = items.filter((i) => i.orientation === ORIENT.V);
  const H = items.filter((i) => i.orientation === ORIENT.H);
  const rows = [];
  const patterns = ['VHH', 'HVH', 'HHV'];
  let p = 0;
  const v = [...V];
  const h = [...H];
  const take = (arr) => (arr.length ? arr.shift() : null);
  while (v.length || h.length) {
    const pat = patterns[p % patterns.length];
    if (pat === 'VHH' && v.length >= 1 && h.length >= 2) rows.push([take(v), take(h), take(h)].filter(Boolean));
    else if (pat === 'HVH' && h.length >= 1 && v.length >= 1 && h.length >= 1) rows.push([take(h), take(v), take(h)].filter(Boolean));
    else if (pat === 'HHV' && h.length >= 2 && v.length >= 1) rows.push([take(h), take(h), take(v)].filter(Boolean));
    else if (h.length >= 3) rows.push([take(h), take(h), take(h)].filter(Boolean));
    else if (v.length >= 1 && h.length >= 1) rows.push([take(v), take(h), take(h) || take(v) || null].filter(Boolean));
    else {
      const last = [take(v) || take(h), take(v) || take(h), take(v) || take(h)].filter(Boolean);
      if (last.length) rows.push(last);
      break;
    }
    p++;
  }
  return rows.filter((r) => r.length > 0);
}

/** Caja visible para object-fit:contain */
function computeImageBox(cardEl, ratio) {
  const fallback = { w: 0, h: 0 };
  if (!cardEl) return fallback;
  const Wc = cardEl.clientWidth || cardEl.getBoundingClientRect().width || 0;
  const Hc = cardEl.clientHeight || cardEl.getBoundingClientRect().height || 0;
  if (!Wc || !Hc) return fallback;
  const r = Number(ratio) > 0 ? Number(ratio) : 1;
  const cRatio = Wc / Hc;
  if (cRatio > r) {
    const imgW = Hc * r;
    return { w: Math.round(imgW), h: Math.round(Hc) };
  } else {
    const imgH = Wc / r;
    return { w: Math.round(Wc), h: Math.round(imgH) };
  }
}

/* ===================== BÚSQUEDA (fuzzy) ===================== */
const _norm = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

function _levenshtein(a, b) {
  a = _norm(a); b = _norm(b);
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[j] = Math.min(
        dp[j] + 1,          // deletion
        dp[j - 1] + 1,      // insertion
        prev + cost         // substitution
      );
      prev = tmp;
    }
  }
  return dp[n];
}

function fuzzyIncludes(needle, haystack) {
  const n = _norm(needle).trim();
  const h = _norm(haystack);
  if (!n) return true;
  if (h.includes(n)) return true;
  const words = n.split(/\s+/).filter(Boolean);
  if (words.length && words.every(w => h.includes(w))) return true;
  const dist = _levenshtein(n, h);
  const threshold = Math.max(2, Math.floor(n.length * 0.25));
  return dist <= threshold;
}
/* =================== FIN BÚSQUEDA (fuzzy) =================== */

const Retratos = () => {
  const history = useHistory();
  const location = useLocation();

  // ======= INITIAL STATE desde URL + sessionStorage (evita "reset" de Ver más) =======
  const initialFromStorage = (() => {
    const params = new URLSearchParams(location.search);
    const urlSort = params.get('sort');
    const urlPage = parseInt(params.get('page') || '1', 10);
    const key = GRID_STATE_KEY(location.pathname, location.search);

    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch {}

    const sort =
      (saved?.sortKey && ['random','recent','az','za','liked'].includes(saved.sortKey)) ? saved.sortKey :
      (urlSort && ['random','recent','az','za','liked'].includes(urlSort)) ? urlSort :
      'random';

    const page =
      (typeof saved?.page === 'number' && saved.page >= 1) ? saved.page :
      (!Number.isNaN(urlPage) && urlPage >= 1) ? urlPage :
      1;

    const query = typeof saved?.query === 'string' ? saved.query : '';

    return {
      sortKey: sort,
      page,
      restoreY: typeof saved?.y === 'number' ? saved.y : null,
      lastId: saved?.lastId || null,
      query,
      hasSaved: !!saved
    };
  })();

  const [sortKey, setSortKey] = useState(() => initialFromStorage.sortKey);
  const [page, setPage] = useState(() => initialFromStorage.page);

  // búsqueda con valor restaurado
  const [query, setQuery] = useState(() => initialFromStorage.query || '');
  const onChangeQuery = (e) => setQuery(e.target.value);
  const clearQuery = () => setQuery('');

  const [rawItems, setRawItems] = useState([]);   // dataset base (orden fijo)
  const [rows, setRows] = useState([]);           // filas visibles
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [favoritos, setFavoritos] = useState(new Set());
  const [showLogin, setShowLogin] = useState(false);

  const startRef = useRef(Date.now());
  const itemsCountRef = useRef(0);

  // índice estable del orden base
  const rawIndexRef = useRef({});

  // refs para corazón / cajas
  const cardsRef = useRef({});
  const ratioRef = useRef({});
  const [imgBox, setImgBox] = useState({});
  const roRef = useRef(null);

  // restauración
  const restoreOnceRef = useRef(false);
  const desiredYRef = useRef(initialFromStorage.restoreY);
  const pendingIdRef = useRef(initialFromStorage.lastId);
  const restoreTriesRef = useRef(0);

  // evita reset por query durante la restauración
  const restoringQueryRef = useRef(true);

  // orden persistente
  const savedOrderRef = useRef(null); // array de _ids en orden

  // Evitar que el cambio de sort por programa dispare reset de page
  const restoringSortRef = useRef(true);

  useLayoutEffect(() => {
    roRef.current = new ResizeObserver((entries) => {
      const patch = {};
      for (const entry of entries) {
        const el = entry.target;
        const id = el.getAttribute('data-id');
        if (!id) continue;
        const ratio = ratioRef.current[id] || 1;
        patch[id] = computeImageBox(el, ratio);
      }
      if (Object.keys(patch).length) setImgBox((prev) => ({ ...prev, ...patch }));
    });
    return () => roRef.current?.disconnect();
  }, []);

  useEffect(() => {
    const ro = roRef.current;
    Object.values(cardsRef.current).forEach((el) => el && ro?.unobserve(el));
    Object.entries(cardsRef.current).forEach(([id, el]) => el && ro?.observe(el));
    const patch = {};
    Object.entries(cardsRef.current).forEach(([id, el]) => {
      const ratio = ratioRef.current[id] || 1;
      patch[id] = computeImageBox(el, ratio);
    });
    if (Object.keys(patch).length) setImgBox((prev) => ({ ...prev, ...patch }));
    return () => { Object.values(cardsRef.current).forEach((el) => el && ro?.unobserve(el)); };
  }, [rows]);

  useEffect(() => {
    return () => {
      const duration = Date.now() - startRef.current;
      if (analytics) {
        logEvent(analytics, 'retratos_engagement', {
          duration_ms: duration, filas: rows.length, items_total: itemsCountRef.current || 0,
        });
      }
    };
  }, [rows.length]);

  useEffect(() => { setFavoritos(readFavs()); }, []);

  // Control de scrollRestoration del navegador
  useEffect(() => {
    let prev = null;
    try {
      if ('scrollRestoration' in window.history) {
        prev = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
      }
    } catch {}
    return () => {
      try {
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = prev || 'auto';
        }
      } catch {}
    };
  }, []);

  // Cargar orden persistente (si existía en esta ruta)
  useEffect(() => {
    const ok = ORDER_STATE_KEY(location.pathname);
    try { savedOrderRef.current = JSON.parse(sessionStorage.getItem(ok) || 'null'); } catch { savedOrderRef.current = null; }
  }, [location.pathname]);

  // Sincronizar URL con estado (ya inicia con lo correcto, así que solo reflejamos)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('sort', sortKey);
    params.set('page', String(page));
    history.replace({ pathname: location.pathname, search: params.toString() });
  }, [sortKey, page, history, location.pathname, location.search]);

  // === Fetch Sanity ===
  useEffect(() => {
    const fetchRetratos = async () => {
      setLoading(true); setErr(null);
      try {
        const strict = `
          *[
            _type == "foto" &&
            !(_id in path("drafts.**")) &&
            (
              lower(categoria) == "${CATEGORIA}" ||
              lower(categoria) match "almas*visibles"
            ) &&
            (
              lower(subcategoria) == "${SUBCATEGORIA_KEY}" ||
              lower(subcategoria) match "retratos*"
            )
          ]|order(_createdAt desc){
            _id, _createdAt, titulo, subcategoria,
            previewImage{asset->{url, metadata{dimensions{width,height,aspectRatio}}}},
            detalleImage{asset->{url, metadata{dimensions{width,height,aspectRatio}}}}
          }
        `;
        let data = await client.fetch(strict);

        if (!Array.isArray(data) || data.length === 0) {
          const relaxed = `
            *[
              _type == "foto" &&
              !(_id in path("drafts.**")) &&
              lower(categoria) match "almas*visibles" &&
              lower(subcategoria) match "retratos*"
            ]|order(_createdAt desc){
              _id, _createdAt, titulo, subcategoria,
              previewImage{asset->{url, metadata{dimensions{width,height,aspectRatio}}}},
              detalleImage{asset->{url, metadata{dimensions{width,height,aspectRatio}}}}
            }
          `;
          data = await client.fetch(relaxed);
        }

        let items = normalizeDocs(data);
        items = await ensureOrientation(items);

        // === ORDEN PERSISTENTE ===
        const orderKey = ORDER_STATE_KEY(location.pathname);
        const savedOrder = Array.isArray(savedOrderRef.current) ? savedOrderRef.current : null;

        if (savedOrder && savedOrder.length) {
          // Reordenar items según el orden guardado; los nuevos van al final
          const pos = new Map(savedOrder.map((id, i) => [id, i]));
          items.sort((a, b) => {
            const pa = pos.has(a._id) ? pos.get(a._id) : Number.MAX_SAFE_INTEGER;
            const pb = pos.has(b._id) ? pos.get(b._id) : Number.MAX_SAFE_INTEGER;
            return pa - pb;
          });
        } else {
          // Primera vez: fijar orden aleatorio y guardarlo
          items = shuffle(items);
          try { sessionStorage.setItem(orderKey, JSON.stringify(items.map(it => it._id))); } catch {}
          savedOrderRef.current = items.map(it => it._id);
        }

        setRawItems(items);
        itemsCountRef.current = items.length;

        // índice estable para 'random'
        const idx = {};
        items.forEach((it, i) => { idx[it._id] = i; });
        rawIndexRef.current = idx;

        if (analytics) logEvent(analytics, 'view_item_list', { item_list_name: 'Retratos', items_count: items.length });
      } catch (e) {
        console.error(e);
        setErr(e?.message || 'Error al cargar');
        setRawItems([]);
        rawIndexRef.current = {};
        itemsCountRef.current = 0;
      } finally { setLoading(false); }
    };
    fetchRetratos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // === Ordenamiento derivado (estable para random) ===
  const sortedItems = useMemo(() => {
    const favs = favoritos;
    let arr = [...rawItems];
    switch (sortKey) {
      case 'recent':
        arr.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        break;
      case 'az':
        arr.sort((a, b) => (a.titulo || '').localeCompare(b.titulo || '', undefined, { sensitivity: 'base' }));
        break;
      case 'za':
        arr.sort((a, b) => (b.titulo || '').localeCompare(a.titulo || '', undefined, { sensitivity: 'base' }));
        break;
      case 'liked':
        arr = arr.filter(it => favs.has(it._id));
        arr.sort((a, b) => (rawIndexRef.current[a._id] ?? 0) - (rawIndexRef.current[b._id] ?? 0));
        break;
      case 'random':
      default:
        arr.sort((a, b) => (rawIndexRef.current[a._id] ?? 0) - (rawIndexRef.current[b._id] ?? 0));
        break;
    }
    return arr;
  }, [rawItems, sortKey, favoritos]);

  /* ================== Filtrado por búsqueda ================== */
  const filteredItems = useMemo(() => {
    if (!query) return sortedItems;
    return sortedItems.filter(it => fuzzyIncludes(query, it.titulo || ''));
  }, [sortedItems, query]);

  // === Layout INMUTABLE de filas (append-only al ver más) ===
  const allRows = useMemo(() => {
    const built = buildRows(filteredItems).map(r => r.filter(Boolean)).filter(r => r.length > 0);
    return built;
  }, [filteredItems]);

  // Cuántos ítems mostrar en esta página
  const visibleCountTarget = useMemo(() => {
    if (sortKey === 'liked') return filteredItems.length;
    return Math.min(page * PAGE_SIZE, filteredItems.length);
  }, [filteredItems.length, page, sortKey]);

  // Filas visibles hasta alcanzar el tope (append-only)
  const visibleRows = useMemo(() => {
    let acc = 0;
    const out = [];
    for (const row of allRows) {
      if (acc >= visibleCountTarget) break;
      out.push(row);
      acc += row.length;
    }
    return out;
  }, [allRows, visibleCountTarget]);

  useEffect(() => { setRows(visibleRows); }, [visibleRows]);

  // Si cambiás el sort manualmente, recién ahí reseteamos a página 1
  useEffect(() => {
    if (restoringSortRef.current) { restoringSortRef.current = false; return; }
    setPage(1);
  }, [sortKey]);

  // No resetear page por query durante la restauración inicial
  useEffect(() => {
    if (restoringQueryRef.current) return;
    setPage(1);
  }, [query]);

  useEffect(() => {
    const t = setTimeout(() => { restoringQueryRef.current = false; }, 0);
    return () => clearTimeout(t);
  }, []);

  const handleClick = (id, title, index) => {
    // Guardar estado de scroll + paginación + sort + id clickeado + query
    const key = GRID_STATE_KEY(location.pathname, location.search);
    const payload = { y: window.scrollY || 0, page, sortKey, lastId: id, query, ts: Date.now() };
    try { sessionStorage.setItem(key, JSON.stringify(payload)); } catch {}

    if (analytics) logEvent(analytics, 'select_content', {
      content_type: 'foto',
      content_id: id,
      content_title: title || '',
      list_name: 'retratos_grid',
      index
    });
    history.push(`/detalle/${id}`);
  };

  const handleToggleFav = (e, id, title) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isLoggedIn()) {
      setShowLogin(true);
      if (analytics) logEvent(analytics, 'login_prompt', { source: 'retratos_fav', content_id: id, content_title: title || '' });
      return;
    }
    setFavoritos((prev) => {
      const willAdd = !prev.has(id);
      const next = new Set(prev);
      if (willAdd) next.add(id); else next.delete(id);
      saveFavs(next);
      if (analytics) logEvent(analytics, willAdd ? 'add_to_favorites' : 'remove_from_favorites', { page: 'retratos', content_type: 'foto', content_id: id, content_title: title || '' });
      return next;
    });
  };

  // ver más / ver menos
  const atEnd = visibleCountTarget >= filteredItems.length;
  const canLoadMore = sortKey !== 'liked' && !atEnd; // hay más para cargar
  const canLoadLess = sortKey !== 'liked' && atEnd && page > 1; // estamos al final y podemos “ver menos”

  const onChangeSort = (e) => {
    const val = e.target.value;
    setSortKey(val);
    restoringSortRef.current = false;
    if (analytics) logEvent(analytics, 'change_sort', { page: 'retratos', sort: val });
  };

  const onLoadMore = () => {
    setPage((p) => p + 1);
    if (analytics) logEvent(analytics, 'load_more', { page: 'retratos', next_page: page + 1 });
  };

  const onLoadLess = () => {
    setPage((p) => Math.max(1, p - 1));
    if (analytics) logEvent(analytics, 'load_less', { page: 'retratos', prev_page: Math.max(1, page - 1) });
  };

  // Restauración robusta del scroll una vez que la grilla ya está pintada
  useEffect(() => {
    if (restoreOnceRef.current) return;
    if (desiredYRef.current == null) return; // no hay nada que restaurar

    try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'; } catch {}

    const desiredY = Math.max(0, desiredYRef.current || 0);
    const key = GRID_STATE_KEY(location.pathname, location.search);

    const tryScroll = () => {
      const doc = document.scrollingElement || document.documentElement;
      const canReach = (doc.scrollHeight - window.innerHeight) >= (desiredY - 4);

      if (canReach) {
        window.scrollTo(0, desiredY);
        if (Math.abs(window.scrollY - desiredY) <= 2) {
          // Éxito
          restoreOnceRef.current = true;
          desiredYRef.current = null;
          pendingIdRef.current = null;
          try { sessionStorage.removeItem(key); } catch {}
          try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto'; } catch {}
          return;
        }
      }

      restoreTriesRef.current += 1;

      // Fallback: después de X intentos, centrar la tarjeta clickeada
      if (restoreTriesRef.current === 40 && pendingIdRef.current) {
        const sel = `[data-id="${pendingIdRef.current}"]`;
        const el = document.querySelector(sel);
        if (el) el.scrollIntoView({ block: 'center' });
      }

      // Cortar después de un máximo
      if (restoreTriesRef.current > 80) {
        restoreOnceRef.current = true;
        desiredYRef.current = null;
        pendingIdRef.current = null;
        try { sessionStorage.removeItem(key); } catch {}
        try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto'; } catch {}
        return;
      }

      requestAnimationFrame(tryScroll);
    };

    requestAnimationFrame(tryScroll);
  }, [rows, location.pathname, location.search]);

  return (
    <div className="retr-wrapper">
      <div className="retr-header">
        <h1 className="retr-title">Retratos</h1>
        <p className="retr-desc">{DESCRIPCION}</p>
      </div>

      <div className="retr-controls">
        {/* Búsqueda */}
        <div className="grid-search">
          <input
            type="search"
            className="grid-search-input"
            placeholder="Buscar por título…"
            value={query}
            onChange={onChangeQuery}
            aria-label="Buscar por título"
          />
          {query && (
            <button type="button" className="grid-search-clear" onClick={clearQuery} aria-label="Limpiar búsqueda">×</button>
          )}
        </div>

        <div className="retr-spacer" />
        <div className="retr-sort">
          <label htmlFor="retr-sort-select" className="retr-sort-label">Ordenar por</label>
          <div className="retr-select-wrap">
            <select id="retr-sort-select" className="retr-sort-select" value={sortKey} onChange={onChangeSort}>
              <option value="random">Aleatorio</option>
              <option value="recent">Más recientes</option>
              <option value="az">A - Z</option>
              <option value="za">Z - A</option>
              <option value="liked">Likeados</option>
            </select>
            <span className="retr-select-caret" aria-hidden>▾</span>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="retr-info">Cargando…</p>
      ) : err ? (
        <p className="retr-info">Ups, algo salió mal: {err}</p>
      ) : rows.length === 0 ? (
        <p className="retr-info">{query ? 'No hay resultados para tu búsqueda.' : 'Todavía no hay retratos para mostrar.'}</p>
      ) : (
        <>
          <div className="retr-rows-wrapper">
            {rows.map((row, rIdx) => {
              const clean = row.filter(Boolean);
              const variant = clean.every(f => f?.orientation === ORIENT.H) ? 'all-h' : clean.every(f => f?.orientation === ORIENT.V) ? 'all-v' : 'mixed';
              return (
                <div className={`retr-row retr-row-${clean.length} ${variant}`} key={`row-${rIdx}`}>
                  {clean.map((f, iIdx) => {
                    const isFav = favoritos.has(f._id);
                    const cls = f.orientation === ORIENT.V ? 'tile v' : 'tile h';
                    ratioRef.current[f._id] = f.ratio || 1;
                    const box = imgBox[f._id] || { w: 0, h: 0 };
                    return (
                      <button
                        key={f._id || `${rIdx}-${iIdx}`}
                        className={`retr-card ${cls}`}
                        data-id={f._id || `${rIdx}-${iIdx}`}
                        ref={(el) => { if (el) cardsRef.current[f._id || `${rIdx}-${iIdx}`] = el; }}
                        style={{ '--ratio': f.ratio || 1 }}
                        onClick={() => handleClick(f._id, f.titulo, iIdx)}
                        onContextMenu={(e) => e.preventDefault()}
                        aria-label={`Ver ${f.titulo || 'Retrato'}`}
                      >
                        <div className="retr-img-viewport">
                          <div className="retr-img-box" style={{ '--box-w': `${box.w || 0}px`, '--box-h': `${box.h || 0}px` }}>
                            <span
                              className="retr-fav-toggle"
                              onClick={(e) => handleToggleFav(e, f._id, f.titulo)}
                              role="button"
                              tabIndex={0}
                              aria-label={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                            >
                              {isFav ? (
                                <svg className="ri-heart-fill" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M12.001 4.529c2.349-2.109 5.979-2.04 8.242.228 2.263 2.268 2.34 5.88.236 8.236L12 21.485 3.521 12.993c-2.104-2.356-2.026-5.974.236-8.236 2.264-2.264 5.888-2.34 8.244-.228z"/>
                                </svg>
                              ) : (
                                <svg className="ri-heart-line" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M12.001 4.52853C14.35 2.42 17.98 2.49 20.2426 4.75736C22.5053 7.02472 22.583 10.637 20.4786 12.993L11.9999 21.485L3.52138 12.993C1.41705 10.637 1.49571 7.01901 3.75736 4.75736C6.02157 2.49315 9.64519 2.41687 12.001 4.52853ZM18.827 6.1701C17.3279 4.66794 14.9076 4.60701 13.337 6.01687L12.0019 7.21524L10.6661 6.01781C9.09098 4.60597 6.67506 4.66808 5.17157 6.17157C3.68183 7.66131 3.60704 10.0473 4.97993 11.6232L11.9999 18.6543L19.0201 11.6232C20.3935 10.0467 20.319 7.66525 18.827 6.1701Z"/>
                                </svg>
                              )}
                            </span>

                            <img
                              src={f.imageUrl}
                              alt={f.titulo || 'Retrato'}
                              className="retr-img"
                              loading="lazy"
                              draggable="false"
                              onDragStart={(e) => e.preventDefault()}
                              onContextMenu={(e) => e.preventDefault()}
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Ver más / Ver menos */}
          {(canLoadMore || canLoadLess) && (
            <div className="retr-more-wrap">
              {!atEnd && (
                <button
                  type="button"
                  className="retr-more-btn retr-more-primary"
                  onClick={onLoadMore}
                >
                  Ver más
                </button>
              )}
              {atEnd && page > 1 && (
                <button
                  type="button"
                  className="retr-more-btn retr-less-btn"
                  onClick={onLoadLess}
                >
                  Ver menos
                </button>
              )}
              <span className="retr-count">Mostrando {visibleCountTarget} / {filteredItems.length}</span>
            </div>
          )}

          {sortKey === 'liked' && visibleCountTarget === 0 && (
            <div className="retr-more-wrap"><span className="retr-count">No tenés favoritos en esta subcategoría todavía.</span></div>
          )}
        </>
      )}

      {/* Modal login */}
      <div className={`retr-login-modal ${showLogin ? 'open' : ''}`} aria-hidden={!showLogin}>
        <div className="retr-login-backdrop" onClick={() => setShowLogin(false)} />
        <div className="retr-login-card" role="dialog" aria-modal="true" aria-labelledby="retr-login-title">
          <button className="retr-login-close" onClick={() => setShowLogin(false)} aria-label="Cerrar">×</button>
          <h3 id="retr-login-title">Necesitás iniciar sesión</h3>
          <p>Para agregar favoritos tenés que estar logueado.</p>
          <a href="/login" className="retr-login-link">Ir a login</a>
        </div>
      </div>
    </div>
  );
};

export default Retratos;
